import { View } from "react-native";
import { gql, useMutation } from "@apollo/client";
import { ActivityIndicator, Text } from "react-native-paper";
import { Button, PopOver } from "../../components";
import { useIntercom } from "../../hooks";
import { PENDING_EMAIL_CHANGE_FRAGMENT } from "./utils";

interface CancelMutation {
  cancelEmailChange: { message: string; success: boolean };
}

export const CANCEL_MUTATION = gql`
  mutation {
    cancelEmailChange {
      message
      success
      currentMember {
        ...PendingEmailChangeFragment
      }
    }
  }
  ${PENDING_EMAIL_CHANGE_FRAGMENT}
`;

export function SessionExpired({ visible, onDismiss }: { visible: boolean; onDismiss(): void }) {
  return (
    <PopOver testID="SessionExpired" showCloseIcon={false} {...{ visible, onDismiss }}>
      <Text className="text-regular-tight font-700 mb-4 text-center">Session Expired</Text>
      <Text className="text-regular-normal text-center">
        The previous session has expired. Your email address/username has not changed.
      </Text>
      <View className="mt-8">
        <Button onPress={onDismiss} mode="secondary">
          Close
        </Button>
      </View>
    </PopOver>
  );
}

export function Failed({ visible, onDismiss }: { visible: boolean; onDismiss(): void }) {
  const intercom = useIntercom();
  return (
    <PopOver testID="Failed" showCloseIcon={false} {...{ visible, onDismiss }}>
      <Text className="text-regular-tight font-700 mb-4 text-center">Unable to Change Email Address</Text>
      <Text className="text-regular-normal text-center">
        Your email address was not able to be updated at this time due to an error. Your email/username has not changed. Please
        try again later or{" "}
        <Text className="underline text-primary" onPress={() => intercom.presentMessageComposer()}>
          contact member services
        </Text>{" "}
        if the issue persists.
      </Text>
      <View className="mt-8">
        <Button onPress={onDismiss} mode="secondary">
          Close
        </Button>
      </View>
    </PopOver>
  );
}

export function DuplicateEmail({
  visible,
  email,
  onDismiss: onDismissCallback,
}: {
  visible: boolean;
  email: String;
  onDismiss(): void;
}) {
  const [cancelChange, { loading }] = useMutation<CancelMutation>(CANCEL_MUTATION);
  async function onDismiss() {
    await cancelChange();
    onDismissCallback();
  }
  const intercom = useIntercom();
  return (
    <PopOver testID="DuplicateEmail" showCloseIcon={false} {...{ visible, onDismiss }}>
      <Text className="text-regular-tight font-700 mb-4 text-center">Unable to Change Email Address</Text>
      <Text className="text-regular-normal text-center">
        Your email/username was not updated because an account already exists with the email: {email}
      </Text>
      <Text className="text-regular-normal text-center mt-4">
        Your current email/username has not changed. If you believe this is an error or for additional assistance, please{" "}
        <Text className="underline text-primary" onPress={() => intercom.presentMessageComposer()}>
          contact member services.
        </Text>{" "}
      </Text>
      <View className="mt-8">
        <Button disabled={loading} loading={loading} onPress={onDismiss} mode="secondary">
          Close
        </Button>
      </View>
    </PopOver>
  );
}

export function AttemptsExhausted({ visible, onDismiss: onDismissCallback }: { visible: boolean; onDismiss(): void }) {
  const [cancelChange, { loading }] = useMutation<CancelMutation>(CANCEL_MUTATION);
  async function onDismiss() {
    await cancelChange();
    onDismissCallback();
  }
  return (
    <PopOver testID="AttemptsExhausted" showCloseIcon={false} {...{ visible, onDismiss }}>
      <Text className="text-regular-tight font-700 mb-4 text-center">Maximum Attempts Exceeded</Text>
      <Text className="text-regular-normal text-center">
        You have exceeded the maximum code entry attempts. Your email address/username has not changed.
      </Text>
      <View className="mt-8">
        <Button disabled={loading} loading={loading} onPress={onDismiss} mode="secondary">
          Cancel Email Change
        </Button>
      </View>
    </PopOver>
  );
}

export function CodeSentInterrupt({ visible, onDismiss, email }: { visible: boolean; onDismiss(): void; email?: string }) {
  return (
    <PopOver testID="CodeSentInterrupt" showCloseIcon={false} {...{ visible, onDismiss }}>
      <Text className="text-regular-tight font-700 mb-2">Code sent.</Text>
      <Text className="text-regular-normal">
        A new verification code has been sent to <Text className="text-regular-normal font-700">{email}</Text>.
      </Text>
      <View className="flex-row justify-end mt-6">
        <Button onPress={onDismiss} mode="secondary" size="small">
          Continue
        </Button>
      </View>
    </PopOver>
  );
}

export function CancelInterrupt({
  visible,
  onDismiss,
  onCancel,
}: {
  visible: boolean;
  onCancel(): void;
  onDismiss(): void;
  email?: string;
}) {
  const [cancelChange, { loading }] = useMutation<CancelMutation>(CANCEL_MUTATION);
  return (
    <PopOver testID="CancelInterrupt" showCloseIcon={false} {...{ visible, onDismiss }}>
      <Text className="text-regular-tight font-700 mb-2">Exit and cancel email change?</Text>
      <Text className="text-regular-normal">If you exit now, your current email change request will be canceled.</Text>
      <View className="flex-row justify-end mt-6">
        <Button
          mode="text"
          size="small"
          className="mr-4"
          loading={loading}
          disabled={loading}
          onPress={async () => {
            await cancelChange();
            onCancel();
          }}
        >
          Exit & Cancel
        </Button>
        <Button onPress={onDismiss} mode="secondary" size="small">
          Continue
        </Button>
      </View>
    </PopOver>
  );
}

export function Updating({ visible }: { visible: boolean }) {
  return (
    <PopOver showCloseIcon={false} dismissable={false} visible={visible} onDismiss={() => {}}>
      <View className="p-5">
        <ActivityIndicator size={30} />
      </View>
      <Text className="text-regular font-500 mt-5 text-center">Updating Email</Text>
    </PopOver>
  );
}
